import React, { useEffect, useMemo, useState } from "react";
import moment from "moment-timezone";
import { FaArrowDown, FaArrowUp, FaChartLine, FaClock, FaSyncAlt, FaTimes } from "react-icons/fa";
import CustomDialog from "../../../components/CustomDialog";
import differentOddService from "../../../services/differentOddService";

const spliter = (value) => {
  return value.split("") ?? "-";
}

const getMovementTone = (currentValue, previousValue) => {
  const current = Number.parseFloat(currentValue);
  const previous = Number.parseFloat(previousValue);

  if (!Number.isFinite(current) || !Number.isFinite(previous)) {
    return "neutral";
  }

  if (current > previous) {
    return "up";
  }

  if (current < previous) {
    return "down";
  }

  return "neutral";
};

const ValueChip = ({ value, tone = "neutral" }) => {
  const toneClass =
    tone === "up"
      ? "border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-500/30 dark:bg-emerald-500/10 dark:text-emerald-300"
      : tone === "down"
        ? "border-rose-200 bg-rose-50 text-rose-700 dark:border-rose-500/30 dark:bg-rose-500/10 dark:text-rose-300"
        : "border-slate-200 bg-white/80 text-slate-700 dark:border-custom_dark_secondary dark:bg-custom_dark_primary/60 dark:text-customFontMilkWhite/80";

  return (
    <span
      className={`inline-flex min-w-[62px] items-center justify-center rounded-full border px-2.5 py-1 text-tiny font-semibold shadow-sm ${toneClass}`}
    >
      {value ?? "N/A"}
      {tone === "up" ? <FaArrowUp className="ml-1 text-xs" /> : null}
      {tone === "down" ? <FaArrowDown className="ml-1 text-xs" /> : null}
    </span>
  );
};

const buildRows = (records = []) =>
  records.map((item, index) => {
    const previous = records[index + 1];

    return {
      id: `${item?.eventId ?? "movement"}-${item?.createdAt ?? index}`,
      time: item?.createdAt ? moment(item.createdAt).format("hh:mm A") : "N/A",
      date: item?.createdAt ? moment(item.createdAt).format("YYYY-MM-DD") : "N/A",
      hdp1: item?.hdp ? item?.hdp[0] : "-",
      hdp2: item?.hdp ? item?.hdp[1] : "-",
      ou1: item?.ou ? item?.ou[0] :  "-",
      ou2: item?.ou ? item?.ou[1] :  "-",

      homeOdd: item?.homeOdd ?? "-",
      awayOdd: item?.awayOdd ?? "-",
      goalOdd: item?.goalOdd ?? "-",
      body909: item?.homeOdd ? item?.homeOdd : item?.awayOdd,

      home555: item?.odds555mix?.odds?.homeOdd ?? "-",
      away555: item?.odds555mix?.odds?.awayOdd ?? "-",
      body555: item?.odds555mix?.odds?.homeOdd ? item?.odds555mix?.odds?.homeOdd : item?.odds555mix?.odds?.awayOdd,
      goalOdd555: item?.odds555mix?.odds?.goalOdd ?? "-",

      homeOddTone: getMovementTone(item?.homeOdd, previous?.homeOdd),
      awayOddTone: getMovementTone(item?.awayOdd, previous?.awayOdd),
      goalOddTone: getMovementTone(item?.goalOdd, previous?.goalOdd),
      eventId: item?.eventId ?? null,
    };
  });

const OddMovementDialog = ({ open, onClose, match, selectedDate }) => {
  const [movementData, setMovementData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    let isMounted = true;

    const fetchMovementData = async () => {
      if (!open || !match?.eventId) {
        return;
      }

      setIsLoading(true);
      setErrorMessage("");

      try {
        const response = await differentOddService.getOddHubMovement({
          eventId: match.eventId,
          selectedDate: selectedDate || moment().format("YYYY-MM-DD"),
        });

        if (!isMounted) {
          return;
        }

        setMovementData(Array.isArray(response?.data?.data) ? response.data.data : []);
      } catch (error) {
        if (!isMounted) {
          return;
        }

        setMovementData([]);
        setErrorMessage(
          error?.response?.data?.message || error?.message || "Unable to load odd movement data.",
        );
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    fetchMovementData();

    return () => {
      isMounted = false;
    };
  }, [open, match?.eventId, selectedDate]);

  const rows = useMemo(() => buildRows(movementData), [movementData]);
  const matchup = match?.base?.[0]?.matchup ?? match?.reference?.[0]?.matchup ?? "Unknown Match";

  return (
    <CustomDialog
      open={open}
      width={700}
      padding={0}
      isMobile={false}
      extraStyle={{ maxHeight: "88vh", overflow: "hidden", borderRadius: 6, border: "none" }}
    >
      <div className="overflow-hidden rounded-[24px] bg-gradient-to-br from-white via-[#f7fbff] to-[#eef5ff] dark:from-custom_dark_primary2 dark:via-custom_dark_primary dark:to-custom_dark_background">
        <div className="border-b border-customLightMilkWhite/80 bg-gradient-to-r from-background via-customAccentBlue to-background px-5 py-4 dark:border-custom_dark_secondary">
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
              <div className="mb-2 inline-flex items-center gap-2 rounded-full bg-white/15 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-white/90 backdrop-blur-sm">
                <FaChartLine className="text-[10px]" />
                Odd Movement
              </div>
              <div className="flex justify-between items-center">
                <h2 className="truncate text-lg font-bold text-white">{matchup}</h2>
                <span className="font-bold text-white">{match?.eventId}</span>
              </div>           
              <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-white/85">
                <span className="inline-flex items-center gap-1 rounded-full bg-white/80 px-3 py-1 text-customPredawn backdrop-blur-sm dark:bg-custom_dark_background dark:text-customWhite">
                  <FaClock className="text-[10px]" />
                  {match?.kickoff ? moment(match.kickoff).format("DD MMM YYYY, hh:mm A") : "TBD"}
                </span>
                <span className="rounded-full bg-white/80 px-3 py-1 text-customPredawn backdrop-blur-sm dark:bg-custom_dark_background dark:text-customWhite">
                  {match?.league ?? "Unknown League"}
                </span>
                <span className="rounded-full bg-white/80 px-3 py-1 text-customPredawn backdrop-blur-sm dark:bg-custom_dark_background dark:text-customWhite">
                  Date {selectedDate || moment().format("YYYY-MM-DD")}
                </span>
              </div>
            </div>

            <button
              type="button"
              onClick={onClose}
              className="rounded-full bg-white/12 p-2 text-white transition-colors hover:bg-white/20 dark:hover:bg-custom_dark_background"
              aria-label="Close odd movement dialog"
            >
              <FaTimes className="h-4 w-4" />
            </button>
          </div>
        </div>

        <div className="px-5 py-4">
          <div className="mb-4 flex items-center justify-between gap-3 rounded-2xl border border-customBorder/30 bg-white/80 px-4 py-3 shadow-sm backdrop-blur-sm dark:border-custom_dark_secondary dark:bg-custom_dark_primary/60">
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-customSecondaryText dark:text-customFontMilkWhite/55">
                Movement Feed
              </p>
              <p className="text-sm font-bold text-customAccent dark:text-customFontMilkWhite">
                {isLoading ? "Refreshing latest changes..." : `${rows.length} snapshots found`}
              </p>
            </div>
            <div className="inline-flex items-center gap-2 rounded-full bg-customSky px-3 py-1.5 text-[11px] font-semibold text-customAccentBlue dark:bg-custom_dark_secondary dark:text-customFontMilkWhite">
              <FaSyncAlt className={isLoading ? "animate-spin" : ""} />
              History ordered by latest update
            </div>
          </div>

          <div className="overflow-hidden rounded-xl border border-customBorder/30 bg-white/85 shadow-[0_18px_40px_rgba(15,23,42,0.08)] dark:border-custom_dark_secondary dark:bg-custom_dark_primary/70">
            <div className="max-h-[56vh] overflow-y-auto">
              <table className="min-w-full border-collapse">
                <thead className="sticky top-0 z-10 bg-primary dark:bg-custom_dark_background">
                  <tr className="text-xs italic uppercase tracking-[0.16em] text-customFontMilkWhite dark:text-customFontMilkWhite/65">
                    <th className="px-4 py-2 text-left font-semibold">Updated At</th>
                    <th className="px-2 py-2 text-center font-semibold min-w-[140px]">HDP</th>
                    <th className="px-2 py-2 text-center font-semibold min-w-[100px]">OU</th>
                    <th className="px-2 py-2 text-center font-semibold min-w-[80px]" colSpan={2}>Sport909</th>
                    {/* <th className="px-2 py-2 text-center font-semibold min-w-[80px]">Away Odd</th> */}
                    <th className="px-2 py-2 text-center font-semibold min-w-[80px]" colSpan={2}>555Mix</th>
                  </tr>
                </thead>
                <tbody>
                  {isLoading ? (
                    <tr>
                      <td colSpan={7} className="px-4 py-12 text-center text-sm font-medium text-customSecondaryText dark:text-customFontMilkWhite/70">
                        Loading movement history...
                      </td>
                    </tr>
                  ) : null}

                  {!isLoading && errorMessage ? (
                    <tr>
                      <td colSpan={7} className="px-4 py-12 text-center text-sm font-medium text-rose-600">
                        {errorMessage}
                      </td>
                    </tr>
                  ) : null}

                  {!isLoading && !errorMessage && rows.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="px-4 py-12 text-center text-sm font-medium text-customSecondaryText dark:text-customFontMilkWhite/70">
                        No movement data found for this event.
                      </td>
                    </tr>
                  ) : null}

                  {!isLoading && !errorMessage
                    ? rows.map((row, index) => (
                        <tr
                          key={row.id}
                          className={`border-t border-t-customHeader/30 text-tiny transition-colors hover:bg-customSky/40 dark:border-custom_dark_secondary dark:hover:bg-custom_dark_secondary/25 ${
                            index % 2 === 0
                              ? "bg-white/70 dark:bg-custom_dark_primary/30"
                              : "bg-[#f8fbff] dark:bg-custom_dark_primary2/45"
                          }`}
                        >
                          <td className="whitespace-nowrap px-4 py-2 dark:text-customFontMilkWhite">
                             <div className="flex flex-col">
                              <span className="font-semibold text-sm text-tiny">{row.time}</span>
                              <span className="text-[0.7rem] text-gray-500 dark:text-customMuted">{row.date}</span>
                            </div>
                          </td>
                          <td className="px-3 py-2 text-center text-customInk font-semibold dark:text-customFontMilkWhite">
                              <div className="flex flex-col">
                              <span>{row.hdp1}</span>
                              <span>{row.hdp2}</span>
                            </div>
                          </td>
                          <td className="px-3 py-2 text-center text-customInk font-semibold dark:text-customFontMilkWhite">
                            <div className="flex flex-col">
                              <span>{row.ou1}</span>
                              <span>{row.ou2}</span>
                            </div>
                          </td>
                          <td className="px-3 py-2 text-center font-semibold"><ValueChip value={row.body909} tone={row.homeOddTone} /></td>
                          {/* <td className="px-3 py-2 text-center"><ValueChip value={row.awayOdd} tone={row.awayOddTone} /></td> */}
                          <td className="px-3 py-2 text-center font-semibold"><ValueChip value={row.goalOdd} tone={row.goalOddTone} /></td>
                          <td className="bg-customVibrantGreen/20 px-3 py-2 text-center font-semibold text-customInk dark:bg-emerald-500/10 dark:text-customFontMilkWhite">
                            <span className="min-w-[62px] rounded-full border border-customHeader/50 px-2.5 py-1 dark:border-emerald-500/20 dark:bg-custom_dark_background">{row.body555}</span>
                          </td>
                          <td className="bg-customVibrantGreen/20 px-3 py-2 text-center font-semibold text-customInk dark:bg-emerald-500/10 dark:text-customFontMilkWhite">
                            <span className="min-w-[62px] rounded-full border border-customHeader/50 px-2.5 py-1 dark:border-emerald-500/20 dark:bg-custom_dark_background">{row.goalOdd555}</span>
                          </td>
                        </tr>
                      ))
                    : null}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </CustomDialog>
  );
};

export default OddMovementDialog;
